import React, { useState } from 'react';
import {
  STATUS_ZONE, ZONE_COLORS,
  isBillable, assemblyNet, assemblyClient, assemblyMargin,
  itemDepositAmount,
  hasAdminAccess,
} from '../utils';
import VendorPaymentsSection from './VendorPaymentsSection';
import BookkeepingAsk from './BookkeepingAsk';
import StateTaxSection from './StateTaxSection';
import SalesTaxDueSection from './SalesTaxDueSection';
import SalesTaxCodesSection from './SalesTaxCodesSection';

const OWNER_EMAIL = 'c@cimonstarr.com';

function fmt(n) {
  if (!n && n !== 0) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
export default function BillingTab({ items, projects, proposals, vendorPayments, setVendorPayments, userEmail, firmSettings, clients }) {
  // Orders in Process / final-invoice generation moved to InvoicesTab.
  // BillingTab is now: Overview / By Project / By Vendor / Vendor Payments /
  // Sales Tax / State Tax / Ask Gilda.
  const [activeSection, setActiveSection] = useState('overview');

  // ── Access control ────────────────────────────────────────────────────────
  // Owner OR any team member with hasAdminAccess can see Finance. Canonical
  // pattern, matches InvoicesTab.jsx:189 (canManagePayments), ItemForm.jsx:1630
  // (canManageCancel), ProposalsTab2.jsx (canFinalizeProposal). Pre-2026-05-10
  // this file was owner-only via strict OWNER_EMAIL equality at three sites;
  // the admin-model RLS pivot (20260510) skipped this file. Brought in line
  // 2026-05-20.
  const isOwner = !!(userEmail && userEmail.toLowerCase() === OWNER_EMAIL.toLowerCase());
  const canAccess = isOwner || hasAdminAccess(userEmail, firmSettings);

  // Toggl / Claude / Designer Hours live in DesignerHoursSection.jsx; the
  // invoice list lives in InvoicesTab. Nothing is loaded here anymore.

  // ── Access control (render-time) ──────────────────────────────────────────
  // Computed at top of function (canAccess = owner OR hasAdminAccess).
  if (!userEmail) return <div style={{ textAlign: 'center', padding: 60, color: '#bbb', fontSize: 13 }}>Loading...</div>;
  if (!canAccess) return (
    <div style={{ textAlign: 'center', padding: 60, color: '#bbb', fontSize: 13 }}>
      <div>This tab requires admin access.</div>
      <div style={{ fontSize: 11, marginTop: 8, color: '#ddd' }}>Signed in as: {userEmail}</div>
    </div>
  );

  const parents = (items || []).filter(it => !it.parentId);
  const billableParents = (items || []).filter(isBillable);

  // ── Financial calculations ────────────────────────────────────────────────
  // allItems is passed for assembly-aware totals: parent price = sum of components
  const allItems = items || [];
  function zoneTotals(its) {
    return its.filter(it => assemblyNet(it, allItems) > 0).reduce((s, it) => ({
      net: s.net + assemblyNet(it, allItems),
      client: s.client + assemblyClient(it, allItems),
      margin: s.margin + assemblyMargin(it, allItems),
      count: s.count + 1,
    }), { net: 0, client: 0, margin: 0, count: 0 });
  }
  const specItems     = billableParents.filter(it => STATUS_ZONE[it.status] === 'spec');
  const quoteItems    = billableParents.filter(it => STATUS_ZONE[it.status] === 'quote');
  const committedItems= billableParents.filter(it => STATUS_ZONE[it.status] === 'committed');
  const specZ      = zoneTotals(specItems);
  const quoteZ     = zoneTotals(quoteItems);
  const committedZ = zoneTotals(committedItems);
  const allZ = { net: specZ.net + quoteZ.net + committedZ.net, client: specZ.client + quoteZ.client + committedZ.client, margin: specZ.margin + quoteZ.margin + committedZ.margin, count: specZ.count + quoteZ.count + committedZ.count };

  const allProposals = proposals || [];
  // Field-name source of truth (from AppContext.rowToProposal):
  //   p.project, p.total_client, p.total_deposit, p.status
  // Legacy refs to p.project_label / p.total_client_price / p.payment_amount return undefined.
  // TODO: client-deposit-received is not currently tracked in any table. The Cash Position
  // metric below approximates "deposit requested" until a real client_payments source exists.
  // Live totals (compute from current item state) -- the stored
  // p.total_deposit / p.total_client go stale when items are edited
  // after the proposal was last saved. Falls back to the stored value
  // if items aren't loaded yet (avoid flashing $0).
  function liveProposalDeposit(p) {
    if (!p || !p.items || p.items.length === 0) return parseFloat(p.total_deposit) || 0;
    var live = p.items.reduce(function(s, id) {
      var it = (allItems || []).find(function(x) { return x.id === id; });
      return it ? s + itemDepositAmount(it, allItems) : s;
    }, 0);
    return live > 0 ? live : (parseFloat(p.total_deposit) || 0);
  }
  function liveProposalClient(p) {
    if (!p || !p.items || p.items.length === 0) return parseFloat(p.total_client) || 0;
    var live = p.items.reduce(function(s, id) {
      var it = (allItems || []).find(function(x) { return x.id === id; });
      return it ? s + assemblyClient(it, allItems) : s;
    }, 0);
    return live > 0 ? live : (parseFloat(p.total_client) || 0);
  }
  const totalDepositsRequested = allProposals
    .filter(p => p.status === 'Sent' || p.status === 'Approved')
    .reduce((s, p) => s + liveProposalDeposit(p), 0);
  const outstandingProposals = allProposals.filter(p => p.status !== 'Approved');
  const outstandingValue = outstandingProposals.reduce((s, p) => s + liveProposalClient(p), 0);
  const allPayments = vendorPayments || [];
  const totalVendorPaid = allPayments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  // Best-available cash-position approximation (deposits requested minus vendor paid).
  const cashPosition = totalDepositsRequested - totalVendorPaid;

  const projectBreakdown = (projects || []).map(proj => {
    const pItems = billableParents.filter(it => it.projectLabel === proj.label || it.project === proj.label);
    const cost = pItems.reduce((s, it) => s + assemblyNet(it, allItems), 0);
    const clientPrice = pItems.reduce((s, it) => s + assemblyClient(it, allItems), 0);
    const pProposals = allProposals.filter(p => p.project === proj.label);
    const depositsIn = pProposals
      .filter(p => p.status === 'Sent' || p.status === 'Approved')
      .reduce((s, p) => s + liveProposalDeposit(p), 0);
    const vendorOut = allPayments.filter(p => p.project_label === proj.label).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    return { label: proj.label, itemCount: pItems.length, cost, clientPrice, margin: clientPrice - cost, depositsIn, vendorOut, net: depositsIn - vendorOut };
  }).filter(p => p.itemCount > 0);

  const vendorBreakdown = {};
  billableParents.forEach(it => {
    if (!it.vendor) return;
    if (!vendorBreakdown[it.vendor]) vendorBreakdown[it.vendor] = { owed: 0, paid: 0, items: 0 };
    vendorBreakdown[it.vendor].owed += assemblyNet(it, allItems);
    vendorBreakdown[it.vendor].items++;
  });
  allPayments.forEach(p => {
    if (!vendorBreakdown[p.vendor_name]) vendorBreakdown[p.vendor_name] = { owed: 0, paid: 0, items: 0 };
    vendorBreakdown[p.vendor_name].paid += parseFloat(p.amount) || 0;
  });
  const vendorRows = Object.entries(vendorBreakdown).map(([name, data]) => ({ name, ...data, balance: data.owed - data.paid })).sort((a, b) => b.balance - a.balance);



  // effCategoryData / effTotalHours / effTotalAmount moved to DesignerHoursSection.

  // Hourly moved to InvoicesTab → Designer Hours (Stage 2). Billing Summary
  // moved to InvoicesTab → Billing Summary (Stage 3). BillingTab is now pure
  // financial visibility + accounts payable.
  // Sales Tax sub-tab (2026-09-13, Cimon: "we can put that on a different
  // tab … should be visible to admin") — SalesTaxDueSection alone: what is
  // owed to each state each month, derived from paid final invoices. No
  // credentials anywhere on it, so it renders for owner + every admin
  // teammate (Dan) under the tab's canAccess gate; the registered-state
  // rows reach non-owners through the credential-free
  // state_tax_registrations view.
  // State Tax sub-tab holds the sales tax code templates and the portal
  // credentials (StateTaxSection — owner-only via its own internal
  // `if (!isOwner)` gate, see StateTaxSection.jsx:59, plus DB-level RLS
  // at 20260510_admin_rls_pivot.sql). The TaxCloud rate cache that used
  // to sit between them was removed 2026-10-17.
  const sections = ['overview', 'projects', 'vendors', 'payments', 'sales_tax', 'state_tax', 'ask'];
  const sectionLabels = { overview: 'Overview', projects: 'By Project', vendors: 'By Vendor', payments: 'Vendor Payments', sales_tax: 'Sales Tax', state_tax: 'State Tax', ask: 'Ask Gilda' };

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Finance</div>
        <span style={{ background: '#1a1a1a', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, letterSpacing: 1 }}>PRIVATE</span>
        <span style={{ background: '#f5f5f5', color: '#555', fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, letterSpacing: 1 }}>✓ SAVED TO SUPABASE</span>
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e0e0e0', marginBottom: 24 }}>
        {sections.map(s => (
          <button key={s} onClick={() => setActiveSection(s)} style={{
            padding: '8px 16px', border: 'none', borderBottom: activeSection === s ? '2px solid #1a1a1a' : '2px solid transparent',
            background: 'none', fontWeight: activeSection === s ? 700 : 400, fontSize: 13,
            color: activeSection === s ? '#1a1a1a' : '#888', cursor: 'pointer',
          }}>{sectionLabels[s]}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {activeSection === 'overview' && (
        <div>
          {/* Zone breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 16 }}>
            {[
              { key: 'spec',      label: 'Specifying',  z: specZ,      dot: ZONE_COLORS.spec,      sub: 'Specifying · Pricing Check' },
              { key: 'quote',     label: 'Quoting',     z: quoteZ,     dot: ZONE_COLORS.quote,     sub: 'Quote Requested → On Proposal' },
              { key: 'committed', label: 'Committed',   z: committedZ, dot: ZONE_COLORS.committed, sub: 'Client Approved → Complete' },
            ].map(({ key, label, z, dot, sub }) => (
              <div key={key} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#222' }}>{label}</div>
                  <div style={{ fontSize: 11, color: '#bbb', marginLeft: 'auto' }}>{z.count} item{z.count !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>{sub}</div>
                {[
                  { label: 'Net Cost',     value: z.net,    color: '#555' },
                  { label: 'Client Price', value: z.client, color: '#111' },
                  { label: 'Margin',       value: z.margin, color: '#555' },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                    <span style={{ fontSize: 11, color: '#888' }}>{row.label}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: row.color }}>{fmt(row.value)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Portfolio totals */}
          <div style={{ background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 10, padding: '18px 22px', marginBottom: 16, display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr', gap: '8px 28px', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Total Portfolio</div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{allZ.count} items</div>
            </div>
            {[
              { label: 'Net Cost',     value: allZ.net },
              { label: 'Client Price', value: allZ.client },
              { label: 'Margin',       value: allZ.margin },
            ].map(row => (
              <div key={row.label}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{row.label}</div>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#111' }}>{fmt(row.value)}</div>
              </div>
            ))}
          </div>

          {/* Cash position */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Deposits Requested', value: fmt(totalDepositsRequested), sub: 'Sent + approved proposals', color: '#111' },
              { label: 'Vendor Payments Out', value: fmt(totalVendorPaid), sub: 'To vendors', color: '#111' },
              { label: 'Cash Position (est.)', value: fmt(cashPosition), sub: 'Requested minus paid out', color: '#111' },
            ].map(card => (
              <div key={card.label} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: card.color }}>{card.value}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginTop: 2 }}>{card.label}</div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{card.sub}</div>
              </div>
            ))}
          </div>

          {outstandingProposals.length > 0 && (
            <div style={{ background: '#fff', border: '0.5px solid #E0E0DC', borderLeft: '3px solid #F5C800', borderRadius: 4, padding: '14px 18px' }}>
              <div style={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#111', marginBottom: 10 }}>
                {outstandingProposals.length} Outstanding Proposal{outstandingProposals.length > 1 ? 's' : ''} — {fmt(outstandingValue)} pending
              </div>
              {outstandingProposals.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#555', padding: '4px 0', borderBottom: '0.5px solid #E0E0DC' }}>
                  <span>{p.proposal_ref} — {p.project}</span>
                  <span style={{ fontWeight: 600 }}>{fmt(p.total_client)} · {p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── BY PROJECT ── */}
      {activeSection === 'projects' && (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1a1a1a' }}>
                {['Project', 'Items', 'Net Cost', 'Client Price', 'Margin', 'Deposits In', 'Vendors Paid', 'Net Cash'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#fff', fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projectBreakdown.map((proj, idx) => (
                <tr key={proj.label} style={{ borderBottom: '1px solid #f0f0f0', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }}>{proj.label}</td>
                  <td style={{ padding: '10px 12px', color: '#666' }}>{proj.itemCount}</td>
                  <td style={{ padding: '10px 12px', color: '#666' }}>{fmt(proj.cost)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: '#111' }}>{fmt(proj.clientPrice)}</td>
                  <td style={{ padding: '10px 12px', color: '#555', fontWeight: 600 }}>{fmt(proj.margin)}</td>
                  <td style={{ padding: '10px 12px', color: '#111', fontWeight: 600 }}>{fmt(proj.depositsIn)}</td>
                  <td style={{ padding: '10px 12px', color: '#555' }}>{fmt(proj.vendorOut)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: '#111' }}>{fmt(proj.net)}</td>
                </tr>
              ))}
              <tr style={{ background: '#f5f5f5', fontWeight: 700 }}>
                <td style={{ padding: '10px 12px' }}>TOTAL</td>
                <td style={{ padding: '10px 12px' }}>{projectBreakdown.reduce((s, p) => s + p.itemCount, 0)}</td>
                <td style={{ padding: '10px 12px' }}>{fmt(projectBreakdown.reduce((s, p) => s + p.cost, 0))}</td>
                <td style={{ padding: '10px 12px', color: '#111' }}>{fmt(projectBreakdown.reduce((s, p) => s + p.clientPrice, 0))}</td>
                <td style={{ padding: '10px 12px', color: '#555' }}>{fmt(projectBreakdown.reduce((s, p) => s + p.margin, 0))}</td>
                <td style={{ padding: '10px 12px', color: '#111' }}>{fmt(projectBreakdown.reduce((s, p) => s + p.depositsIn, 0))}</td>
                <td style={{ padding: '10px 12px', color: '#555' }}>{fmt(projectBreakdown.reduce((s, p) => s + p.vendorOut, 0))}</td>
                <td style={{ padding: '10px 12px', color: '#111' }}>{fmt(cashPosition)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── BY VENDOR ── */}
      {activeSection === 'vendors' && (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1a1a1a' }}>
                {['Vendor', 'Items', 'Total Owed', 'Total Paid', 'Balance Remaining'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#fff', fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vendorRows.map((v, idx) => (
                <tr key={v.name} style={{ borderBottom: '1px solid #f0f0f0', background: v.balance > 0 ? '#fffdf0' : idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{v.name}</td>
                  <td style={{ padding: '10px 12px', color: '#666' }}>{v.items}</td>
                  <td style={{ padding: '10px 12px', color: '#666' }}>{fmt(v.owed)}</td>
                  <td style={{ padding: '10px 12px', color: '#111', fontWeight: 600 }}>{fmt(v.paid)}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: v.balance > 0 ? '#111' : '#1A5C38' }}>
                    {v.balance > 0 ? fmt(v.balance) : '✓ Paid'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── VENDOR PAYMENTS ── */}
      {activeSection === 'payments' && <VendorPaymentsSection />}

      {/* ── STATE TAX (project rates: owner + admin; registrations:
          owner-only via StateTaxSection's internal gate) ── */}
      {/* ── SALES TAX (owed per state per period: owner + admin) ── */}
      {activeSection === 'sales_tax' && <SalesTaxDueSection />}

      {activeSection === 'state_tax' && (
        <>
          <SalesTaxCodesSection />
          {canAccess && <StateTaxSection />}
        </>
      )}

      {/* ── ASK GILDA — BOOKKEEPING ── */}
      {activeSection === 'ask' && <BookkeepingAsk />}

    </div>
  );
}
